import { useState, useEffect } from 'react';
import { Plus, Ban, CheckCircle2, Pencil } from 'lucide-react';
import { useUsers, useCreateUser, useUpdateUser, useDisableUser, useEnableUser, useChangeOwnPassword } from '../hooks/useUsers.js';
import { useClients } from '../hooks/useClients.js';
import { useAuthStore } from '../store/auth.store.js';
import PageHeader from '../components/PageHeader.jsx';
import Table from '../components/Table.jsx';
import Badge from '../components/Badge.jsx';
import Button from '../components/Button.jsx';
import Modal from '../components/Modal.jsx';
import Input from '../components/Input.jsx';
import PasswordInput from '../components/PasswordInput.jsx';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter.jsx';

function CreateUserModal({ open, onClose, isSuperadmin, clients }) {
  const createUser = useCreateUser();
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'admin', clientId: '' });
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await createUser.mutateAsync({ ...form, clientId: form.clientId || undefined });
      onClose();
      setForm({ email: '', password: '', name: '', role: 'admin', clientId: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear el usuario');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Nuevo usuario">
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <PasswordInput label="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
        <PasswordStrengthMeter password={form.password} />
        <Input label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        {isSuperadmin && (
          <>
            <Input as="select" label="Rol" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="admin">admin</option>
              <option value="superadmin">superadmin</option>
            </Input>
            {form.role === 'admin' && (
              <Input as="select" label="Cliente" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} required>
                <option value="">Seleccionar…</option>
                {clients?.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
              </Input>
            )}
          </>
        )}
        {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={createUser.isPending}>Crear</Button>
        </div>
      </form>
    </Modal>
  );
}

const EMPTY_PASSWORD_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

function EditUserModal({ user, onClose, isSuperadmin, meId, clients }) {
  const updateUser = useUpdateUser();
  const changePassword = useChangeOwnPassword();
  const [form, setForm] = useState(null);
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM);
  const [error, setError] = useState(null);
  const isSelf = !!user && user.id === meId;
  const passwordsMismatch = passwordForm.confirmPassword.length > 0 && passwordForm.newPassword !== passwordForm.confirmPassword;
  const passwordChangeIncomplete = !!passwordForm.newPassword && (!passwordForm.currentPassword || passwordsMismatch || passwordForm.newPassword.length < 8);

  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name, role: user.role, clientId: user.client_id || '' });
    setPasswordForm(EMPTY_PASSWORD_FORM);
  }, [user?.id]);

  const handleClose = () => { setForm(null); setError(null); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (passwordForm.newPassword) {
      if (!passwordForm.currentPassword) {
        setError('Ingresá tu contraseña actual para cambiarla');
        return;
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setError('Las contraseñas nuevas no coinciden');
        return;
      }
    }
    try {
      await updateUser.mutateAsync({ id: user.id, ...form, clientId: form.clientId || undefined });
      if (isSelf && passwordForm.newPassword) {
        await changePassword.mutateAsync(passwordForm);
      }
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al editar el usuario');
    }
  };

  return (
    <Modal open={!!user} onClose={handleClose} title={`Editar ${user?.email || ''}`} maxWidth="max-w-lg">
      {form && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          {isSuperadmin && (
            <>
              <Input as="select" label="Rol" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="admin">admin</option>
                <option value="superadmin">superadmin</option>
              </Input>
              {form.role === 'admin' && (
                <Input as="select" label="Cliente" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} required>
                  <option value="">Seleccionar…</option>
                  {clients?.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                </Input>
              )}
            </>
          )}
          {isSelf && (
            <div className="pt-1 border-t border-border space-y-3">
              <p className="text-xs font-medium text-text-secondary pt-2">Cambiar contraseña (opcional)</p>
              <PasswordInput
                label="Contraseña actual"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              />
              <PasswordInput
                label="Contraseña nueva"
                value={passwordForm.newPassword}
                minLength={8}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              />
              <PasswordStrengthMeter password={passwordForm.newPassword} />
              <PasswordInput
                label="Repetir contraseña nueva"
                value={passwordForm.confirmPassword}
                minLength={8}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              />
              {passwordsMismatch && <p className="text-xs text-red-400">Las contraseñas no coinciden</p>}
            </div>
          )}
          {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={handleClose}>Cancelar</Button>
            <Button type="submit" disabled={passwordChangeIncomplete} loading={updateUser.isPending || changePassword.isPending}>Guardar</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default function Users() {
  const { user: me } = useAuthStore();
  const isSuperadmin = me?.role === 'superadmin';
  const { data, isLoading } = useUsers();
  const clientsQuery = useClients();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Usuarios"
        subtitle="Acceso al panel de administración"
        actions={isSuperadmin && <Button onClick={() => setCreateOpen(true)}><Plus size={16} /> Nuevo usuario</Button>}
      />
      <div className="card">
        <Table
          loading={isLoading}
          data={data}
          emptyMessage="Sin usuarios"
          columns={[
            { key: 'email', label: 'Email' },
            { key: 'name', label: 'Nombre' },
            { key: 'role', label: 'Rol', render: (r) => <Badge variant={r.role === 'superadmin' ? 'success' : 'neutral'}>{r.role}</Badge> },
            { key: 'client_name', label: 'Cliente', render: (r) => r.client_name || '—' },
            { key: 'status', label: 'Estado', render: (r) => <Badge variant={r.status === 'active' ? 'success' : 'danger'}>{r.status}</Badge> },
            {
              key: 'actions', label: '', render: (r) => (
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={() => setEditingUser(r)} className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-accent" title="Editar">
                    <Pencil size={15} />
                  </button>
                  {r.id !== me?.id && (
                    r.status === 'active' ? (
                      <button onClick={() => disableUser.mutate(r.id)} className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-red-400" title="Deshabilitar">
                        <Ban size={15} />
                      </button>
                    ) : (
                      <button onClick={() => enableUser.mutate(r.id)} className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-emerald-400" title="Habilitar">
                        <CheckCircle2 size={15} />
                      </button>
                    )
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} isSuperadmin={isSuperadmin} clients={clientsQuery.data} />
      <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} isSuperadmin={isSuperadmin} meId={me?.id} clients={clientsQuery.data} />
    </div>
  );
}
